import type { Freshness, PublicSnapshot } from "../../shared/contracts";
import { freshnessAt, VESSEL_RETENTION_MS } from "../../shared/freshness";

function ageFreshness(published: Freshness, elapsed: Freshness): Freshness {
  if (published === "unknown" || published === "stale") return published;
  if (elapsed === "stale" || elapsed === "unknown") return elapsed;
  return published === "delayed" ? published : elapsed;
}

/** Age server evidence using elapsed client time, without trusting the client's UTC clock. */
export function ageSnapshot(source: PublicSnapshot, elapsedMs: number): PublicSnapshot {
  if (source.dataMode === "demo" || elapsedMs <= 0) return source;
  const generatedAtMs = Date.parse(source.generatedAt);
  const nowMs = generatedAtMs + elapsedMs;
  const transportFreshness = freshnessAt(generatedAtMs, nowMs);
  const expired = transportFreshness === "stale";
  const bridges = source.bridges.map((bridge) => {
    const freshness = ageFreshness(bridge.freshness, freshnessAt(
      bridge.observedAt === null ? null : Date.parse(bridge.observedAt), nowMs,
    ));
    const state = freshness === "stale" || freshness === "unknown" ? "unknown" : bridge.state;
    return freshness === bridge.freshness && state === bridge.state ? bridge : { ...bridge, freshness, state };
  });
  const vessels = source.vessels.filter((vessel) => nowMs - Date.parse(vessel.observedAt) < VESSEL_RETENTION_MS);
  const retainedIds = new Set(vessels.map((vessel) => vessel.id));
  const bridgeFreshness = bridges.find((bridge) => bridge.id === "brickell")?.freshness ?? "unknown";
  const freshness = ageFreshness(source.forecast.freshness, ageFreshness(transportFreshness, bridgeFreshness));
  const forecastExpired = freshness === "stale" || freshness === "unknown";
  return {
    ...source,
    bridges,
    forecast: {
      ...source.forecast,
      freshness,
      ...(forecastExpired ? { state: "unavailable", openingWindow: null, confidencePercent: null, reason: null } as const : {}),
    },
    weather: expired ? { ...source.weather, freshness: ageFreshness(source.weather.freshness, "stale") } : source.weather,
    roadTraffic: expired ? { ...source.roadTraffic, freshness: ageFreshness(source.roadTraffic.freshness, "stale") } : source.roadTraffic,
    vesselActivity: expired ? {
      ...source.vesselActivity, status: "unavailable", freshness: "stale",
    } : source.vesselActivity,
    vessels,
    vesselGroups: source.vesselGroups.filter((group) =>
      [...group.tugIds, ...group.towIds].every((id) => retainedIds.has(id))),
  };
}
